import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Placeholder, Spinner } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMagnifyingGlass } from "@fortawesome/free-solid-svg-icons";
import Swal from "sweetalert2";
import { crmPostventaV2Api } from "../../crmpostventa-v2/crmPostventaV2Api";
import LostTerminalAlertCard from "./LostTerminalAlertCard";
import {
  filterLostTerminalRows,
  normalizeLostTerminalDetail,
  normalizeLostTerminalItem,
  normalizeText,
} from "./lostTerminalAlertsUtils";
import "./lost-terminal-alerts.css";

const FETCH_PAGE_SIZE = 200;
const SEARCH_DEBOUNCE_MS = 300;

export default function LostTerminalAlertsSection({
  processCode,
  processSlug,
  title,
  globalFilters,
  emptyMessage,
  onManage,
  onAssign,
  onRelease,
  onTake,
  onMutationNonce = 0,
}) {
  const [rowsState, setRowsState] = useState({
    loading: true,
    rows: [],
    total: 0,
    error: "",
    permissions: null,
  });
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [expandedId, setExpandedId] = useState(null);
  const [detailsCache, setDetailsCache] = useState({});
  const [loadingDetailId, setLoadingDetailId] = useState(null);
  const [detailErrors, setDetailErrors] = useState({});
  const [copiedMessageId, setCopiedMessageId] = useState(null);
  const [liveMessage, setLiveMessage] = useState("");

  useEffect(() => {
    const timeout = window.setTimeout(() => setDebouncedSearch(search), SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timeout);
  }, [search]);

  const loadRows = async () => {
    setRowsState((prev) => ({ ...prev, loading: true, error: "" }));
    try {
      let pageNumber = 1;
      let total = 0;
      let mergedRows = [];

      while (pageNumber === 1 || mergedRows.length < total) {
        const response = await crmPostventaV2Api.obtenerAlertas({
          procesoCodigo: processCode,
          fechaReferencia: globalFilters?.fechaReferencia,
          prioridad: globalFilters?.prioridad || undefined,
          estado: globalFilters?.estado || undefined,
          texto: globalFilters?.texto || undefined,
          pagina: pageNumber,
          tamanoPagina: FETCH_PAGE_SIZE,
        });

        const currentRows = Array.isArray(response?.filas) ? response.filas : [];
        total = Number(response?.total || currentRows.length || 0);
        mergedRows = mergedRows.concat(currentRows);

        if (!currentRows.length || mergedRows.length >= total) {
          setRowsState({
            loading: false,
            rows: mergedRows.map((item) => normalizeLostTerminalItem({ ...item, procesoSlug: processSlug })),
            total,
            permissions: response?.permisos || null,
            error: "",
          });
          return;
        }

        pageNumber += 1;
      }
    } catch {
      setRowsState({
        loading: false,
        rows: [],
        total: 0,
        permissions: null,
        error: "No pudimos cargar las terminales extraviadas.",
      });
    }
  };

  useEffect(() => {
    loadRows();
  }, [globalFilters?.estado, globalFilters?.fechaReferencia, globalFilters?.prioridad, globalFilters?.texto, processCode, processSlug]);

  useEffect(() => {
    if (onMutationNonce > 0) {
      loadRows();
      setDetailsCache({});
    }
  }, [onMutationNonce]);

  const filteredRows = useMemo(
    () => filterLostTerminalRows(rowsState.rows, debouncedSearch),
    [debouncedSearch, rowsState.rows]
  );

  useEffect(() => {
    if (!filteredRows.length) {
      setExpandedId(null);
      return;
    }

    setExpandedId((current) => (filteredRows.some((item) => item.id === current) ? current : filteredRows[0].id));
  }, [filteredRows]);

  const loadDetail = async (item) => {
    const cacheKey = String(item.id);
    if (detailsCache[cacheKey]) {
      return detailsCache[cacheKey];
    }

    setLoadingDetailId(item.id);
    setDetailErrors((prev) => ({ ...prev, [cacheKey]: "" }));
    try {
      const detailResponse = await crmPostventaV2Api.obtenerDetalle(item.processSlug || processSlug, item.id);
      const normalizedDetail = normalizeLostTerminalDetail(detailResponse);
      setDetailsCache((prev) => ({ ...prev, [cacheKey]: normalizedDetail }));
      return normalizedDetail;
    } catch (error) {
      const message = error?.message || "No pudimos cargar el detalle de la terminal extraviada.";
      setDetailErrors((prev) => ({ ...prev, [cacheKey]: message }));
      return null;
    } finally {
      setLoadingDetailId(null);
    }
  };

  useEffect(() => {
    const current = filteredRows.find((item) => item.id === expandedId);
    if (current && !detailsCache[String(current.id)]) {
      loadDetail(current);
    }
  }, [expandedId, filteredRows]);

  const handleToggle = async (item) => {
    setExpandedId((current) => (current === item.id ? null : item.id));
    if (!detailsCache[String(item.id)]) {
      await loadDetail(item);
    }
  };

  const handleCopyMessage = async (message, itemId = null) => {
    try {
      await navigator.clipboard.writeText(message || "");
      setCopiedMessageId(itemId);
      setLiveMessage("Mensaje copiado");
      window.setTimeout(() => {
        setCopiedMessageId(null);
        setLiveMessage("");
      }, 1800);
    } catch {
      Swal.fire(title, "No se pudo copiar el mensaje sugerido.", "error");
    }
  };

  return (
    <section className="lost-terminal-alerts-section">
      <h3>{title}</h3>

      <div className="lost-terminal-alerts__search">
        <FontAwesomeIcon icon={faMagnifyingGlass} />
        <input
          type="search"
          className="form-control"
          placeholder="Buscar por comercio, CUIT, terminal, responsable, teléfono..."
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          aria-label="Buscar terminales extraviadas"
        />
      </div>

      <div className="lost-terminal-alerts__live-region" aria-live="polite">{liveMessage}</div>

      {rowsState.loading ? (
        <div className="lost-terminal-alerts__list">
          {Array.from({ length: 3 }, (_, index) => (
            <div key={index} className={`lost-terminal-alerts__skeleton ${index === 1 ? "is-expanded" : ""}`}>
              <Placeholder as="div" animation="glow"><Placeholder xs={12} /></Placeholder>
              <Placeholder as="div" animation="glow"><Placeholder xs={8} /></Placeholder>
              {index === 1 ? (
                <div className="lost-terminal-alerts__skeleton-detail">
                  <Placeholder as="div" animation="glow"><Placeholder xs={12} /></Placeholder>
                  <Placeholder as="div" animation="glow"><Placeholder xs={10} /></Placeholder>
                </div>
              ) : null}
            </div>
          ))}
        </div>
      ) : rowsState.error ? (
        <Alert variant="warning" className="lost-terminal-alerts__error">
          <div>{rowsState.error}</div>
          <Button type="button" variant="outline-secondary" size="sm" onClick={loadRows}>Reintentar</Button>
        </Alert>
      ) : !rowsState.rows.length ? (
        <Alert variant="secondary" className="mb-0">
          {emptyMessage || "No hay terminales extraviadas pendientes."}
        </Alert>
      ) : !filteredRows.length ? (
        <Alert variant="secondary" className="mb-0">
          No se encontraron terminales extraviadas con la búsqueda actual.
        </Alert>
      ) : (
        <div className="lost-terminal-alerts__list">
          {filteredRows.map((item) => (
            <LostTerminalAlertCard
              key={item.id}
              item={item}
              expanded={expandedId === item.id}
              detail={detailsCache[String(item.id)] || null}
              detailLoading={loadingDetailId === item.id}
              detailError={detailErrors[String(item.id)] || ""}
              copied={copiedMessageId === item.id}
              onToggle={() => handleToggle(item)}
              onManage={onManage}
              onTake={onTake}
              onAssign={onAssign}
              onRelease={onRelease}
              onCopyMessage={(message) => handleCopyMessage(message, item.id)}
            />
          ))}
        </div>
      )}

      {rowsState.permissions && !rowsState.permissions?.puedeGestionarPostventaGeneral && !rowsState.permissions?.puedeAdministrarPostventa ? (
        <div className="lost-terminal-alerts__permission-note">No tenés permisos para gestionar terminales extraviadas.</div>
      ) : null}
    </section>
  );
}
